import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { DunningMessageDraft, DunningMessageDraftsResponse } from "../types"

export type DunningHumanReviewResult = {
  outcome: "human_review"
  generationRequestId: number
  reason: string
  body: null
}

const draftsBase = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/message_drafts`

/** idempotencyKey はクライアントで都度生成（再生成 = 新しいキーで POST） */
const newIdempotencyKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`

const errorMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback

/**
 * AIドラフト（dunning_message_drafts）:
 * - POST   … 生成（再生成も同エンドポイント。旧 current は superseded になる）
 * - PATCH  … editedBody 保存（送信候補文面の編集）
 * - POST :id/send … 督促チャット経路で送信（LINE 配信 + 接触カウント）
 * - POST :id/discard … 破棄（status: discarded。本文は履歴として保持する）
 * - GET    … 履歴（superseded / discarded / sent 含む・新しい順）
 * 生成失敗（502）・有効プロンプトなし（422）はサーバのメッセージをそのまま表示する。
 */
export const useDunningMessageDrafts = (id: number) => {
  const { getWithToken, postWithToken, patchWithToken } = useSmoothApi()

  const historySwr = useSWR(
    draftsBase(id),
    async (fetchUrl: string) => {
      const response = await getWithToken(fetchUrl)
      return response.data as DunningMessageDraftsResponse
    },
    // 同じケースを開き直したときも取り直す（_app.tsx の既定 revalidateIfStale: false では
    // 前回表示以降の生成・送信が履歴に出ない）
    { revalidateOnFocus: false, revalidateIfStale: true }
  )

  const [isGenerating, setIsGenerating] = useState(false)
  const [isSendingDraft, setIsSendingDraft] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)

  const generate = async (): Promise<DunningMessageDraft | DunningHumanReviewResult | undefined> => {
    setIsGenerating(true)
    setDraftError(null)
    try {
      // model は通常送らない（サーバ既定のモデルを使う）
      const response = await postWithToken(draftsBase(id), {
        idempotencyKey: newIdempotencyKey(),
      })
      await historySwr.mutate()
      return response.data as DunningMessageDraft | DunningHumanReviewResult
    } catch (e) {
      setDraftError(errorMessage(e, "AIドラフトの生成に失敗しました。"))
      return undefined
    } finally {
      setIsGenerating(false)
    }
  }

  /** 送信候補文面の保存（PATCH editedBody） */
  const saveEditedBody = async (
    draftId: number,
    editedBody: string
  ): Promise<DunningMessageDraft | undefined> => {
    setDraftError(null)
    try {
      const response = await patchWithToken(`${draftsBase(id)}/${draftId}`, { editedBody })
      return response.data as DunningMessageDraft
    } catch (e) {
      setDraftError(errorMessage(e, "ドラフトの保存に失敗しました。"))
      return undefined
    }
  }

  /** 👍/👎 評価（同じ値の付け直し不可の代わりに null で取り消し）。教師データとして保存される */
  const setFeedback = async (
    draftId: number,
    feedback: "good" | "bad" | null
  ): Promise<DunningMessageDraft | undefined> => {
    setDraftError(null)
    try {
      const response = await patchWithToken(`${draftsBase(id)}/${draftId}/feedback`, { feedback })
      await historySwr.mutate()
      return response.data as DunningMessageDraft
    } catch (e) {
      setDraftError(errorMessage(e, "評価の保存に失敗しました。"))
      return undefined
    }
  }

  /**
   * ドラフト送信（通常チャット送信と同じガード・接触カウント）
   * @param options.countsAsContact false のとき「架電内容の事務連絡」として督促回数に数えずに送る。省略時は数える。
   */
  const sendDraft = async (
    draftId: number,
    options?: { countsAsContact?: boolean }
  ): Promise<DunningMessageDraft | undefined> => {
    setIsSendingDraft(true)
    setDraftError(null)
    try {
      const payload = options?.countsAsContact === false ? { countsAsContact: false } : {}
      const response = await postWithToken(`${draftsBase(id)}/${draftId}/send`, payload)
      await historySwr.mutate()
      return response.data as DunningMessageDraft
    } catch (e) {
      setDraftError(errorMessage(e, "ドラフトの送信に失敗しました。"))
      return undefined
    } finally {
      setIsSendingDraft(false)
    }
  }

  const discard = async (draftId: number): Promise<boolean> => {
    setDraftError(null)
    try {
      await postWithToken(`${draftsBase(id)}/${draftId}/discard`, {})
      // 進行中の古いGETもSWRのmutation境界で無効にする。
      await historySwr.mutate(
        (current) =>
          current && {
            ...current,
            data: current.data.map((entry) =>
              entry.id === draftId ? { ...entry, status: "discarded" as const } : entry
            ),
          },
        { revalidate: false }
      )
      return true
    } catch (e) {
      // 422（既に送信済み・再生成済みで current でない）は再試行しても成功しないため、
      // サーバの案内文（再読み込みを促す）をそのまま出す。
      setDraftError(errorMessage(e, "破棄できませんでした。再試行してください"))
      return false
    }
  }

  return {
    discard,
    drafts: historySwr.data?.data ?? [],
    humanReview: historySwr.data?.humanReview ?? null,
    isHistoryLoading: !historySwr.data && !historySwr.error,
    refresh: () => historySwr.mutate(),
    generate,
    saveEditedBody,
    setFeedback,
    sendDraft,
    isGenerating,
    isSendingDraft,
    draftError,
  }
}
